import React, { useState, useEffect, useCallback } from 'react';
import './EmployeeManagement.css';
import authService from '../services/auth.service';
import apiService from '../services/api.service';
import EmployeeCredentialsModal from './EmployeeCredentialsModal';

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [currentEmployee, setCurrentEmployee] = useState({
    id: null,
    name: '',
    surname: '',
    position: '',
    phone: '',
    email: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isActive: true,
    notes: ''
  });

  // Credentials modal state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Filtering
  const [nameFilter, setNameFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (nameFilter.trim()) filters.name = nameFilter.trim();
      if (positionFilter.trim()) filters.position = positionFilter.trim();
      if (statusFilter !== 'all') filters.isActive = statusFilter === 'active';

      const response = await apiService.getEmployees(filters);
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Çalışanlar yüklenirken bir hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [nameFilter, positionFilter, statusFilter]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const resetForm = () => {
    setCurrentEmployee({
      id: null,
      name: '',
      surname: '',
      position: '',
      phone: '',
      email: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      isActive: true,
      notes: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentEmployee(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddNew = () => {
    resetForm();
    setFormMode('add');
    setShowForm(true);
    setError(null);
  };

  const handleEdit = (employee) => {
    const formattedEmployee = {
      ...employee,
      startDate: employee.startDate ? new Date(employee.startDate).toISOString().split('T')[0] : '',
      endDate: employee.endDate ? new Date(employee.endDate).toISOString().split('T')[0] : '',
      // Ensure phone and email are also initialized as strings for the form
      phone: employee.phone || '',
      email: employee.email || '',
      notes: employee.notes || '',
    };
    setCurrentEmployee(formattedEmployee);
    setFormMode('edit');
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!currentEmployee.name.trim() || !currentEmployee.surname.trim() || !currentEmployee.position.trim() || !currentEmployee.startDate) {
      setError("Lütfen tüm zorunlu alanları (*) doldurun.");
      return;
    }

    // Prepare the data for the API call
    const employeeDataForApi = {
      ...currentEmployee,
      // Convert empty string endDate to null, as the backend likely expects null for an empty date
      endDate: currentEmployee.endDate === '' ? null : currentEmployee.endDate,
      // Optional: If other string fields should be null if empty, you can do the same:
      // phone: currentEmployee.phone === '' ? null : currentEmployee.phone,
      email: currentEmployee.email === '' ? null : currentEmployee.email,
      // notes: currentEmployee.notes === '' ? null : currentEmployee.notes,
    };

    try {
      if (formMode === 'add') {
        await apiService.createEmployee(employeeDataForApi);
      } else {
        if (!employeeDataForApi.id) {
            throw new Error("Düzenlenecek personel için ID bulunamadı.");
        }
        await apiService.updateEmployee(employeeDataForApi.id, employeeDataForApi);
      }
      setShowForm(false);
      resetForm();
      fetchEmployees();
    } catch (err) {
      console.error('Error saving employee:', err);
      setError('Personel kaydedilirken bir hata oluştu: ' + (err.response?.data?.error || err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu personeli silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }
    setError(null);
    try {
      await apiService.deleteEmployee(id);
      fetchEmployees();
      if (showForm && currentEmployee.id === id) {
        setShowForm(false);
        resetForm();
      }
    } catch (err) {
      console.error('Error deleting employee:', err);
      setError('Personel silinirken bir hata oluştu: ' + (err.response?.data?.error || err.response?.data?.message || err.message));
    }
  };

  // Handle setting employee credentials
  const handleSetCredentials = (employee) => {
    setSelectedEmployee(employee);
    setShowCredentialsModal(true);
    setError(null);
  };

  // Handle successful credential update
  const handleCredentialsSuccess = (updatedEmployee) => {
    // Update the employee in the list with the new employeeId
    setEmployees(prevEmployees =>
      prevEmployees.map(emp =>
        emp.id === updatedEmployee.id ? { ...emp, employeeId: updatedEmployee.employeeId } : emp
      )
    );
  };

  const handleFilterReset = () => {
    setNameFilter('');
    setPositionFilter('');
    setStatusFilter('all');
  };

  const user = authService.getUser();
  const isAdmin = user?.role === 'admin';

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '-';
    try {
        // Assuming dateString is in "YYYY-MM-DD" format or a full ISO string
        // For "YYYY-MM-DD", JavaScript's Date constructor interprets it as UTC.
        // To display correctly in local time without shifts for just date part:
        const parts = dateString.split('T')[0].split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) -1; // Month is 0-indexed
            const day = parseInt(parts[2], 10);
            const date = new Date(Date.UTC(year, month, day));
             return new Intl.DateTimeFormat('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
        }
        // Fallback for full ISO strings (though API seems to return just date)
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
    } catch (e) {
        console.warn("Error formatting date:", dateString, e);
        return dateString; // return original string if formatting fails
    }
  };


  return (
    <div className="employee-management">
      <div className="section-header">
        <h2>Personel Yönetimi</h2>
        {isAdmin && (
          <button
            className="add-button"
            onClick={handleAddNew}
          >
            <span className="button-text-main">Yeni Personel Ekle</span>
          </button>
        )}
      </div>

      <div className="filters-container">
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="nameFilterEmp">İsim Ara:</label>
            <input
              id="nameFilterEmp"
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="İsim veya soyisim..."
            />
          </div>
          <div className="filter-group">
            <label htmlFor="positionFilterEmp">Pozisyon:</label>
            <input
              id="positionFilterEmp"
              type="text"
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              placeholder="Pozisyon..."
            />
          </div>
          <div className="filter-group">
            <label htmlFor="statusFilterEmp">Durum:</label>
            <select
              id="statusFilterEmp"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
          </div>
          <button
            className="reset-button"
            onClick={handleFilterReset}
          >
            Filtreleri Sıfırla
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : (
        <div className="employees-container">
          {employees.length === 0 && !error ? (
            <div className="no-data">Kayıtlı personel bulunamadı. Filtrelerinizi kontrol edin veya yeni personel ekleyin.</div>
          ) : (
            <table className="employees-table">
              <thead>
                <tr>
                  <th>İsim</th>
                  <th>Soyisim</th>
                  <th>Pozisyon</th>
                  <th>Telefon</th>
                  <th>Başlangıç</th>
                  <th>Durum</th>
                  <th>Giriş Bilgileri</th>
                  <th style={{ width: 'auto', minWidth: '120px', textAlign: 'center' }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(employee => (
                  <tr key={employee.id} className={!employee.isActive ? 'inactive-row' : ''}>
                    <td>{employee.name}</td>
                    <td>{employee.surname}</td>
                    <td>{employee.position}</td>
                    <td>{employee.phone || '-'}</td>
                    <td>{formatDateForDisplay(employee.startDate)}</td>
                    <td>
                      <span className={`status-badge ${employee.isActive ? 'active' : 'inactive'}`}>
                        {employee.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      {employee.employeeId ? (
                        <span className="credential-status has-credentials">{employee.employeeId}</span>
                      ) : (
                        <span className="credential-status no-credentials">Tanımlanmadı</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="view-button"
                          onClick={() => handleEdit(employee)}
                          title="Personel Detayları/Düzenle"
                        >
                          <span className="icon">👁</span>
                          <span className="button-text">Detay</span>
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              className="edit-button"
                              onClick={() => handleEdit(employee)}
                              title="Personel Düzenle"
                            >
                              <span className="icon">✏️</span>
                              <span className="button-text">Düzenle</span>
                            </button>
                            <button
                              className="credential-button"
                              onClick={() => handleSetCredentials(employee)}
                              title={employee.employeeId ? "Giriş Bilgilerini Güncelle" : "Giriş Bilgileri Tanımla"}
                            >
                              <span className="icon">🔑</span>
                              <span className="button-text">{employee.employeeId ? "Şifre" : "Tanımla"}</span>
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDelete(employee.id)}
                              title="Personel Sil"
                            >
                              <span className="icon">🗑</span>
                              <span className="button-text">Sil</span>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Employee form modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{formMode === 'add' ? 'Yeni Personel Ekle' : 'Personel Bilgilerini Düzenle'}</h3>
              <button
                className="close-button"
                onClick={() => setShowForm(false)}
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nameEmpForm">İsim *</label>
                  <input
                    type="text"
                    id="nameEmpForm"
                    name="name"
                    value={currentEmployee.name}
                    onChange={handleInputChange}
                    required
                    autoFocus={formMode === 'add'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="surnameEmpForm">Soyisim *</label>
                  <input
                    type="text"
                    id="surnameEmpForm"
                    name="surname"
                    value={currentEmployee.surname}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="positionEmpForm">Pozisyon *</label>
                  <input
                    type="text"
                    id="positionEmpForm"
                    name="position"
                    value={currentEmployee.position}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phoneEmpForm">Telefon</label>
                  <input
                    type="tel"
                    id="phoneEmpForm"
                    name="phone"
                    value={currentEmployee.phone} // Removed || '' as handleEdit initializes it
                    onChange={handleInputChange}
                    placeholder="Örn: 5551234567"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="emailEmpForm">E-posta</label>
                  <input
                    type="email"
                    id="emailEmpForm"
                    name="email"
                    value={currentEmployee.email} // Removed || ''
                    onChange={handleInputChange}
                    placeholder="personel@example.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="startDateEmpForm">Başlangıç Tarihi *</label>
                  <input
                    type="date"
                    id="startDateEmpForm"
                    name="startDate"
                    value={currentEmployee.startDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="endDateEmpForm">Bitiş Tarihi</label>
                  <input
                    type="date"
                    id="endDateEmpForm"
                    name="endDate"
                    value={currentEmployee.endDate} // Removed || ''
                    onChange={handleInputChange}
                    min={currentEmployee.startDate || undefined}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label htmlFor="isActiveCheckboxEmp">
                    <input
                      type="checkbox"
                      id="isActiveCheckboxEmp"
                      name="isActive"
                      checked={currentEmployee.isActive}
                      onChange={handleInputChange}
                    />
                    Aktif Çalışan
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="notesEmpForm">Notlar</label>
                <textarea
                  id="notesEmpForm"
                  name="notes"
                  value={currentEmployee.notes} // Removed || ''
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Ek bilgiler..."
                ></textarea>
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowForm(false)}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="save-button"
                >
                  {formMode === 'add' ? 'Kaydet' : 'Güncelle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Employee credentials modal */}
      {showCredentialsModal && selectedEmployee && (
        <EmployeeCredentialsModal
          employee={selectedEmployee}
          onClose={() => setShowCredentialsModal(false)}
          onSuccess={handleCredentialsSuccess}
        />
      )}
    </div>
  );
};

export default EmployeeManagement;